// PURE visible-shot gate (UX §5 caps table "Gallery visible shots: 4/s per Gallery, 8/s total — payout
// aggregated, no anim"; "1 coin per *visible* shot"): ONE decision per `fire` event, shared by the board FX
// (recoil, muzzle, confetti: render/juice/boardFx.ts) and the DOM FX (the shot's coin: fx/DomFx.ts), so a coin
// never flies for a shot the board did not show. The first caller decides at its clock time and the decision
// is remembered per event object (the GameController fans the same batch out to both sinks); later callers
// read it. In the app the DOM layer sees every batch first (it handles events synchronously, the board at the
// next frame), so the windows run on one clock. Sliding 1 s windows per Gallery and in all.

export interface ShotCaps {
  /** juice.json caps.galleryShotsPerSec. */
  perSec: number;
  /** juice.json caps.galleryShotsTotalPerSec. */
  totalPerSec: number;
}

export class ShotGate {
  private readonly caps: ShotCaps;
  private decided = new WeakMap<object, boolean>();
  private readonly perBld = new Map<number, number[]>();
  private all: number[] = [];

  constructor(caps: ShotCaps) {
    this.caps = caps;
  }

  /** Whether shot `e` (a `fire` event) is shown; decided once per event, at the first caller's `now`. */
  visible(e: { readonly bid: number }, now: number): boolean {
    const known = this.decided.get(e);
    if (known !== undefined) return known;
    const recent = (this.perBld.get(e.bid) ?? []).filter((t) => now - t < 1000);
    this.all = this.all.filter((t) => now - t < 1000);
    const ok = recent.length < this.caps.perSec && this.all.length < this.caps.totalPerSec;
    if (ok) {
      recent.push(now);
      this.all.push(now);
    }
    if (recent.length > 0) this.perBld.set(e.bid, recent);
    else this.perBld.delete(e.bid);
    this.decided.set(e, ok);
    return ok;
  }

  /** A removed / merged-away Gallery: its window goes. */
  forget(bid: number): void {
    this.perBld.delete(bid);
  }

  /** A rebuild / load: every window and decision goes. */
  clear(): void {
    this.decided = new WeakMap();
    this.perBld.clear();
    this.all = [];
  }
}
